import { MDSActionMenuWithContent } from "@/mds/components/menu";
import {
  autoUpdate,
  detectOverflow,
  flip,
  FloatingNode,
  FloatingPortal,
  offset,
  safePolygon,
  shift,
  size,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useHover,
  useInteractions,
} from "@floating-ui/react";
import { memo, useEffect, useMemo, useState } from "react";
import { StyledMenuWrapper } from "./floating-action-menu.style";
import { MDSFloatingActionMenuDropdownProps } from "./floating-action-menu-dropdown.types";
// This will be moved into @mds
// eslint-disable-next-line no-restricted-imports
import { ZIndex } from "@/domains/design/constants";

/**
 * @deprecated
 * This component is deprecated.
 * Please prefer to use the components in @/mds/components/menu instead
 */
export const MDSFloatingActionMenuDropdown = memo<MDSFloatingActionMenuDropdownProps>(
  function MDSFloatingActionMenuDropdown({
    clientRect,
    contentList,
    placement,
    offsetOptions,
    zIndex,
    menuRef,
    onOpenChange,
    rootBoundary = "viewport",
  }) {
    const nodeId = useFloatingNodeId();

    const [availableHeight, setAvailableHeight] = useState(0);

    const { x, y, refs, strategy, context } = useFloating({
      whileElementsMounted: autoUpdate,
      nodeId,
      open: true,
      onOpenChange: (...args) => {
        if (onOpenChange) {
          onOpenChange(...args);
        }
      },
      placement: placement ?? "bottom-start",
      middleware: [
        offsetOptions ? offset(offsetOptions[0], offsetOptions[1]) : undefined,
        flip({
          rootBoundary,
          padding: 0,
        }),
        shift({
          rootBoundary,
          padding: 0,
        }),
        {
          name: "avoidOverflow",
          async fn(middlewareArgs) {
            const overflow = await detectOverflow(middlewareArgs, {
              rootBoundary,
            });

            if (overflow.bottom <= 0 && overflow.top <= 0) {
              return {};
            }

            return size({
              rootBoundary,
              apply({ availableHeight }) {
                // For some reason floating-ui is flickering between 9px and the actual height
                // of the content. Besides the flickering sometimes the 9px locks the height
                // first, resulting in a very short dropdown. Delay/debounce and updating
                // @floating-ui didn't fix the issue, so this hack is the most reliable solution
                // for now.
                if (availableHeight < 20) return;

                setAvailableHeight(availableHeight - 8);
              },
            }).fn(middlewareArgs);
          },
        },
      ],
    });

    useEffect(() => {
      if (!clientRect) return;

      refs.setPositionReference({
        getBoundingClientRect() {
          return clientRect;
        },
      });

      // Recalculate the available height when position or content changes.
      setAvailableHeight(0);
    }, [refs, clientRect, contentList.length]);

    const { getReferenceProps, getFloatingProps } = useInteractions([
      useHover(context, {
        move: false,
        handleClose: safePolygon(),
        delay: {
          close: 100,
        },
      }),
      useDismiss(context),
    ]);

    const portalContent = useMemo(() => {
      return (
        <StyledMenuWrapper
          ref={refs.setFloating}
          $maxHeight={availableHeight}
          style={{
            position: strategy,
            opacity: x != null && y != null ? 1 : 0,
            top: y,
            left: x,
            zIndex: zIndex ?? ZIndex.Dropdown,
            display: contentList.length === 0 ? "none" : undefined,
          }}
          {...getFloatingProps()}
        >
          <MDSActionMenuWithContent ref={menuRef} contentList={contentList} />
        </StyledMenuWrapper>
      );
    }, [
      contentList,
      getFloatingProps,
      refs.setFloating,
      strategy,
      x,
      y,
      zIndex,
      availableHeight,
      menuRef,
    ]);

    return (
      <FloatingNode id={nodeId}>
        <div ref={refs.setReference} {...getReferenceProps()}></div>
        <FloatingPortal>{portalContent}</FloatingPortal>
      </FloatingNode>
    );
  }
);
